import { idsOf } from "gather-common/dist/src/public/fpHelpers";
export class LatestSpeakerStack {
    speakers;
    latestSpeakers;
    maxSpeakers;
    pausedStreamRemoved;
    pausedSpeakerHistory;
    historyLimitMs;
    priorityChanged;
    constructor(maxSpeakers, historyLimitMs = 0, pausedStreamRemoved) {
        this.speakers = new Set();
        this.latestSpeakers = [];
        this.maxSpeakers = maxSpeakers;
        this.pausedStreamRemoved = pausedStreamRemoved;
        this.pausedSpeakerHistory = [];
        this.historyLimitMs = historyLimitMs;
        this.priorityChanged = false;
    }
    adjustSpeakerList(id, enabled) {
        if (enabled) {
            if (!this.speakers.has(id)) {
                this.speakers.add(id);
                if (this.maxSpeakers <= 0 || this.latestSpeakers.length < this.maxSpeakers) {
                    this.latestSpeakers.push({ id, paused: false, hasSpoken: false, highestPriority: false });
                }
            }
        }
        else if (this.speakers.has(id)) {
            this.speakers.delete(id);
            for (const [i, speaker] of this.latestSpeakers.entries()) {
                if (speaker.id === id) {
                    if (speaker.paused && this.pausedStreamRemoved) {
                        this.pausedStreamRemoved(id);
                    }
                    this.latestSpeakers.splice(i, 1);
                    break;
                }
            }
        }
        this.updateHistory();
    }
    addHighestPrioritySpeakers(ids) {
        const highestPrioritySpeakers = ids
            .filter((id) => this.speakers.has(id))
            .map((id) => this.getLatestSpeakerById(id) ?? {
            id,
            paused: false,
            hasSpoken: false,
            highestPriority: false,
        })
            .map((speaker) => ({
            ...speaker,
            highestPriority: true,
        }));
        const otherSpeakers = this.latestSpeakers
            .filter((speaker) => !ids.includes(speaker.id))
            .map((speaker) => ({
            ...speaker,
            highestPriority: false,
        }));
        this.latestSpeakers = [...highestPrioritySpeakers, ...otherSpeakers];
    }
    clearHighestPriorityStreams() {
        this.latestSpeakers
            .filter((speaker) => speaker.highestPriority)
            .forEach((speaker) => {
            speaker.highestPriority = false;
        });
    }
    addLatestSpeaker(id) {
        if (!this.speakers.has(id))
            return;
        const numHighestPrioritySpeakers = this.getLatestSpeakersWithHighestPriority().length;
        const index = this.getLatestSpeakerIndexById(id);
        const speaker = this.latestSpeakers[index] ?? {
            id,
            paused: false,
            hasSpoken: true,
            highestPriority: false,
        };
        speaker.hasSpoken = true;
        if (speaker.highestPriority || index === numHighestPrioritySpeakers)
            return;
        if (index > 0) {
            this.latestSpeakers.splice(index, 1);
        }
        this.latestSpeakers.splice(numHighestPrioritySpeakers, 0, speaker);
        if (this.maxSpeakers > 0 && this.latestSpeakers.length > this.maxSpeakers) {
            this.latestSpeakers.length = this.maxSpeakers;
        }
        this.updateHistory();
    }
    getLatestSpeakers() {
        return this.latestSpeakers;
    }
    getLatestSpeakersWithHighestPriority = () => idsOf(this.latestSpeakers.filter((speaker) => speaker.highestPriority));
    getLatestSpeakerById(id) {
        const filteredSpeakers = this.latestSpeakers.filter((speaker) => speaker.id === id);
        return filteredSpeakers[0];
    }
    getLatestSpeakerIndexById(id) {
        return this.latestSpeakers.map((speaker) => speaker.id).indexOf(id);
    }
    getLatestSpeakerIds() {
        return this.latestSpeakers.map((speaker) => speaker.id);
    }
    setLatestSpeakerPaused(id, paused) {
        for (const speaker of this.latestSpeakers) {
            if (speaker.id === id) {
                speaker.paused = paused;
                this.updateHistory();
                return;
            }
        }
    }
    clear() {
        this.latestSpeakers = [];
        this.speakers.clear();
        this.pausedSpeakerHistory = [];
        this.priorityChanged = false;
    }
    updateHistory() {
        this.priorityChanged = true;
        if (!this.historyLimitMs)
            return;
        const now = Date.now();
        const latestPausedSpeakers = this.latestSpeakers
            .map((s, indexOnStack) => ({ id: s.id, indexOnStack, paused: s.paused }))
            .filter((s) => s.paused)
            .map((s) => ({ id: s.id, indexOnStack: s.indexOnStack }));
        if (latestPausedSpeakers.length === 0) {
            this.pausedSpeakerHistory = [];
            return;
        }
        const historyLength = this.pausedSpeakerHistory.unshift({
            timestamp: now,
            latestSpeakers: latestPausedSpeakers,
        });
        let newHistoryLength = 0;
        for (let i = historyLength - 1; i >= 0; --i) {
            const historyEntry = this.pausedSpeakerHistory[i];
            if (!historyEntry || now - historyEntry.timestamp < this.historyLimitMs) {
                break;
            }
            newHistoryLength = i + 1;
        }
        if (newHistoryLength > 0) {
            this.pausedSpeakerHistory.length = newHistoryLength;
        }
    }
    getSwappableSpeakers() {
        if (this.pausedSpeakerHistory.length === 0)
            return;
        const latestUnpausedSpeakers = this.latestSpeakers
            .map((s, indexOnStack) => ({ id: s.id, indexOnStack, paused: s.paused }))
            .filter((s) => !s.paused);
        const numberUnpaused = latestUnpausedSpeakers.length;
        if (numberUnpaused === 0)
            return;
        const oldestUnpaused = latestUnpausedSpeakers[numberUnpaused - 1];
        if (!oldestUnpaused || oldestUnpaused.indexOnStack < numberUnpaused)
            return;
        const unpausedId = oldestUnpaused.id;
        const now = Date.now();
        const filteredPausedSpeakers = this.latestSpeakers.filter((speaker) => {
            if (!speaker.paused || !speaker.hasSpoken)
                return false;
            for (const historyEntry of this.pausedSpeakerHistory) {
                const indexOfId = historyEntry.latestSpeakers.map((s) => s.id).indexOf(speaker.id);
                if ((historyEntry.latestSpeakers[indexOfId]?.indexOnStack ?? 0) >= numberUnpaused)
                    return false;
                if (now - historyEntry.timestamp >= this.historyLimitMs) {
                    break;
                }
            }
            return true;
        });
        const pausedId = filteredPausedSpeakers[0]?.id;
        return pausedId ? { pausedId, unpausedId } : undefined;
    }
    destroy() {
        this.clear();
    }
    removeSpeaker(id) {
        if (this.speakers.has(id)) {
            this.speakers.delete(id);
            const index = this.getLatestSpeakerIndexById(id);
            this.latestSpeakers.splice(index, 1);
        }
    }
    hasPriorityChanged() {
        const priorityChanged = this.priorityChanged;
        this.priorityChanged = false;
        return priorityChanged;
    }
}
//# sourceMappingURL=LatestSpeakerStack.js.map